import { APP_INITIALIZER, Injectable, Provider } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, Select, Store } from '@ngxs/store';
import {
  AppFlowType,
  FlowState,
  GotoAcademicRegistration,
  GotoCheckoutFlowAction,
  GotoCommercialCheckout,
  GotoPurchaseInformation,
  GotoSubscriptionManagement,
  SubscriptionBillingState
} from '@platform-frontend/checkout';
import { GotoScaSpinner } from '@platform-frontend/marketplace';
import { LoadingState } from '@platform-frontend/ui-tools';
import {
  ActionClass,
  ClearNotifications,
  GotoAction,
  GotoError,
  InvalidStateError,
  Navigate,
  NavigationUtilityService,
  SimpleRouterNavigationExtras,
  SimpleRouterState,
  filterDispatchedAction
} from '@platform-frontend/util';
import { Buffer } from 'buffer';
import { EMPTY, Observable, combineLatest, merge } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { WebCheckoutIntegrationService } from './integration/web-checkout-integration.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  constructor(
    private actions$: Actions,
    private navigationUtilityService: NavigationUtilityService,
    private store: Store,
    private webCheckoutIntegrationService: WebCheckoutIntegrationService
  ) {}

  @Select(FlowState.flowType)
  flowType$: Observable<AppFlowType>;

  @Select(SubscriptionBillingState.stashRequiresConfirmation)
  stashRequiresConfirmation$: Observable<boolean>;

  init(): void {
    merge(
      this.navigateOn(GotoAcademicRegistration, action => this.buildAcademicRegistrationRoute(action)),
      this.navigateOnGotoFlowCheckout(GotoCommercialCheckout, action => this.buildCheckoutRoute('/checkout/', action)),
      this.navigateOnGotoFlowCheckout(GotoPurchaseInformation, action => this.buildCheckoutRoute('/purchase-information/', action)),
      this.navigateOnGotoFlowCheckout(GotoSubscriptionManagement, action => this.buildCheckoutRoute('/subscription-management/', action)),
      this.navigateOn(GotoScaSpinner, () => this.buildScaSpinnerRoute()),
      this.navigateOn(GotoError, () => '/error', true),
      this.navigationUtilityService.handleRefreshWindow(() => this.webCheckoutIntegrationService.restart())
    )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  private navigateOnGotoFlowCheckout<T extends GotoCheckoutFlowAction>(action: ActionClass<T>, route: (dispatchedAction: T) => string): Observable<unknown> {
    return this.navigateOn(action, route, false, action => action.extras);
  }

  private navigateOn<T extends GotoAction>(
    action: ActionClass<T>,
    route: (dispatchedAction: T) => string,
    addReferrer: boolean = false,
    extras: (dispatchedAction: T) => SimpleRouterNavigationExtras | undefined = dispatchedAction => dispatchedAction.extras
  ): Observable<unknown> {
    return this.actions$.pipe(
      filterDispatchedAction([action]),
      withLatestFrom(this.webCheckoutIntegrationService.isApplicationVisible()),
      switchMap(([dispatchedAction, isApplicationVisible]) =>
        isApplicationVisible
          ? this.navigateWithExtras(route(dispatchedAction), addReferrer, extras(dispatchedAction))
          : this.clearNotificationsIfAllowed(dispatchedAction.extras?.clearNotifications)
      )
    );
  }

  private navigateWithExtras(route: string, addReferrer: boolean, extras?: SimpleRouterNavigationExtras): Observable<unknown> {
    return this.navigationUtilityService.navigateWithExtras(route, this.store.selectSnapshot(LoadingState.isLoading), addReferrer, extras);
  }

  private clearNotificationsIfAllowed(clearNotifications?: boolean): Observable<unknown> {
    return clearNotifications !== false ? this.store.dispatch(new ClearNotifications()) : EMPTY;
  }

  private buildAcademicRegistrationRoute(dispatchedAction: GotoAcademicRegistration): string {
    return '/academic-registration/' + Buffer.from(JSON.stringify(dispatchedAction.startParams)).toString('base64') + '/academic-registration';
  }

  private buildCheckoutRoute<T extends GotoCheckoutFlowAction>(routePrefix: string, dispatchedAction: T): string {
    const startParams = Buffer.from(JSON.stringify(dispatchedAction.startParams)).toString('base64');
    return routePrefix + startParams + '/overview';
  }

  private buildScaSpinnerRoute(): string {
    const url = this.store.selectSnapshot(SimpleRouterState.urlWithoutParams);
    const regex = new RegExp('^(' + '/checkout+/[^/]+' + '|' + '/subscription-management/[^/]+' + ')');
    const parsedUrl = url.match(regex);

    if (parsedUrl === null) {
      throw new InvalidStateError('Cannot navigate to sca-spinner from ' + url);
    }

    return `${parsedUrl[0]}/sca-authentication`;
  }

  gotoConfirmationOrOverview(activatedRoute: ActivatedRoute, clearNotifications?: boolean): Observable<void> {
    return combineLatest([this.flowType$, this.stashRequiresConfirmation$]).pipe(
      take(1),
      map(([flowType, stashRequiresConfirmation]) => this.getConfirmationOrOverviewUrl(flowType, stashRequiresConfirmation)),
      switchMap(url => this.store.dispatch(new Navigate([url], { relativeTo: activatedRoute, clearNotifications })))
    );
  }

  private getConfirmationOrOverviewUrl(flowType: AppFlowType, stashRequiresConfirmation: boolean): string {
    if (flowType === 'SUBSCRIPTION_MANAGEMENT' && stashRequiresConfirmation) {
      return '../update-confirmation';
    } else if (flowType === 'SUBSCRIPTION_MANAGEMENT') {
      return '../overview';
    } else {
      return '../overview';
    }
  }
}

export const NavigationServiceInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (navigationService: NavigationService) => () => navigationService.init(),
  deps: [NavigationService],
  multi: true
};
